import { analyticsService } from "@/common/services/analytics/analytics.service";
import { cn } from "@/common/utils/css.utils";
import { PROMOTIONS_ENABLED } from "@/features/promotions/config";
import { ProductCard } from "@/features/search/components/product-card/ProductCard";
import { ResultsEmpty } from "@/features/search/components/results/ResultsEmpty";
import { useSearchContext } from "@/features/search/context/SearchContext";
import {
  getSearchListId,
  toAnalyticsProduct,
} from "@/features/search/utils/analytics.utils";
import { DEFAULT_LIMIT } from "@/features/search/utils/search-params.utils";

const PRELOAD_COUNT = 4;

/** Responsive product grid. Shows skeletons on first load, empty state on no hits. */
export const ResultsGrid = () => {
  const { query } = useSearchContext();
  const products = query.data?.data ?? [];
  // Retain organic results, but never deliver an unchecked or failed refresh's
  // paid presentation and potentially expired measurement tickets.
  const hidePromotions =
    query.isPlaceholderData || query.isFetching || query.isError;

  const gridClass = cn(
    "grid grid-cols-2 gap-y-8 md:grid-cols-3 xl:grid-cols-4",
    PROMOTIONS_ENABLED ? "gap-x-8" : "gap-x-4"
  );

  if (query.isLoading) {
    return (
      <div className={gridClass}>
        {Array.from({ length: DEFAULT_LIMIT }, (_, index) => (
          <ProductCard.Skeleton key={index} />
        ))}
      </div>
    );
  }

  if (!products.length) {
    return <ResultsEmpty />;
  }

  const listId = getSearchListId(query.data);
  const pageInfo = query.data?.pageInfo;

  return (
    <>
      <div
        className={cn(
          gridClass,
          "transition-opacity",
          query.isPlaceholderData && "opacity-60"
        )}
      >
        {products.map((product, index) => (
          <ProductCard
            key={product.id}
            product={hidePromotions ? { ...product, promotion: null } : product}
            priority={index < PRELOAD_COUNT}
            /*
             * Position is 1-based and page-local, as it was on the gateway PLP — the number
             * says where in the visible list the shopper clicked, which is the point of it.
             *
             * `searchMeta` has no `queryId` yet: the service doesn't surface Typesense's
             * (WHO-5050). Page and limit go now so the shape is already right when it does.
             */
            onSelect={() =>
              analyticsService.trackSelectItem(
                index + 1,
                listId,
                listId,
                toAnalyticsProduct(product),
                {
                  page: pageInfo?.page,
                  limit: pageInfo?.limit,
                  /* This grid only ever renders search results, and `listId` is a taxonomy slug the
                   * other implementation never emits — so the flag, not the id, is what a search
                   * metric can compare across the two. */
                  fromSearch: true,
                }
              )
            }
          />
        ))}
      </div>
    </>
  );
};
